import React, { useMemo, memo } from 'react'
import { useMediaQuery } from 'react-responsive'

const MasonryLayout = memo(
  ({ children, containerClass = '', columnClass = '', columnsOnMobile = 2, columnsOnWeb, itemKey }) => {
    const isMobile = useMediaQuery({ maxWidth: 800 })

    // Efficiently calculate and memoize columns to prevent recalculation on re-renders
    const columns = useMemo(() => {
      const columnsNo = isMobile ? columnsOnMobile : columnsOnWeb || 4
      const result = Array.from({ length: columnsNo }, () => [])

      // Only iterate if there are children
      if (!children || !children.length) return result

      // Use React.Children.toArray for better efficiency when dealing with children
      const validChildren = React.Children.toArray(children).filter((child) => child && React.isValidElement(child))

      // Distribute items across columns
      validChildren.forEach((child, index) => {
        result[index % columnsNo].push(child)
      })

      return result
    }, [children, isMobile, columnsOnMobile, columnsOnWeb])

    // If there are no children, return an empty container with the appropriate styling
    if (!children || React.Children.count(children) === 0) {
      return <div className={`${containerClass} flex gap-2 min-h-[50vh]`}></div>
    }

    return (
      <div className={`${containerClass} flex gap-2 min-h-[50vh]`}>
        {columns.map((column, columnIndex) => (
          <div
            key={`masonry-column-${columnIndex}`}
            className={`flex flex-col justify-start content-stretch flex-1 gap-2 ${columnClass}`}
          >
            {column.map((item, itemIndex) => {
              // Use provided itemKey function if available, otherwise use index
              const key = itemKey ? itemKey(item.props, itemIndex) : `item-${columnIndex}-${itemIndex}`
              return React.cloneElement(item, { key })
            })}
          </div>
        ))}
      </div>
    )
  }
)

MasonryLayout.displayName = 'MasonryLayout'

export default MasonryLayout
